import React from 'react';
import { ReviewsContent, GalleryContent, WhyChooseUsContent } from '../../hooks/usePersistentContent';

interface JsonLdProps {
  data: Record<string, unknown>;
}

const JsonLd: React.FC<JsonLdProps> = ({ data }) => (
  <script
    type="application/ld+json"
    dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
  />
);

/* ---------- AggregateRating + individual Reviews ---------- */
export const ReviewsStructuredData: React.FC<{ reviews: ReviewsContent }> = ({ reviews }) => {
  if (!reviews.items || reviews.items.length === 0) return null;

  const ratingValues = reviews.items.map((r) => r.rating ?? 5);
  const avg = ratingValues.reduce((a, b) => a + b, 0) / ratingValues.length;

  const aggregateSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'Touch Event',
    url: 'https://touchevent.site',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: avg.toFixed(1),
      bestRating: '5',
      worstRating: '1',
      ratingCount: reviews.items.length,
    },
    review: reviews.items.map((r) => ({
      '@type': 'Review',
      author: { '@type': 'Person', name: r.name },
      reviewRating: {
        '@type': 'Rating',
        ratingValue: String(r.rating ?? 5),
        bestRating: '5',
      },
      reviewBody: r.quote,
    })),
  };

  return <JsonLd data={aggregateSchema} />;
};

/* ---------- Service schema for gallery/product items ---------- */
export const ServicesStructuredData: React.FC<{ gallery: GalleryContent }> = ({ gallery }) => {
  if (!gallery.items || gallery.items.length === 0) return null;

  const services = gallery.items.map((item) => ({
    '@type': 'Service',
    name: item.title || 'שירות עיסוי מקצועי',
    description: item.description || '',
    image: item.image,
    provider: {
      '@type': 'LocalBusiness',
      name: 'Touch Event',
    },
  }));

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'שירותי עיסוי מקצועי - Touch Event',
    itemListElement: services.map((svc, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      item: svc,
    })),
  };

  return <JsonLd data={schema} />;
};

/* ---------- FAQ schema from WhyChooseUs items ---------- */
export const FAQStructuredData: React.FC<{ why: WhyChooseUsContent }> = ({ why }) => {
  if (!why.items || why.items.length === 0) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: why.items.map((item) => ({
      '@type': 'Question',
      name: item.title,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.body,
      },
    })),
  };

  return <JsonLd data={schema} />;
};

/* ---------- BreadcrumbList for sub-pages ---------- */
export const BreadcrumbStructuredData: React.FC<{ items: Array<{ name: string; url: string }> }> = ({ items }) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: item.name,
      item: { '@type': 'WebPage', '@id': item.url, name: item.name },
    })),
  };

  return <JsonLd data={schema} />;
};
